import { LoadingSpinner } from "@bluefalcon/components-package";
import { Permission, Role, RoleCreateRequest, RoleUpdateRequest } from "@bluefalcon/users-management-client-fetch";
import { Box, Button, Flex, Table, TableCaption, Tbody, Td, Th, Thead, Tr, useDisclosure } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { iamService } from "../../services";
import { AddEditIAMModal } from "../../modals/create-iam.modal";
import { ManagePermissionsModal } from "../../modals/manage-permissions.modal";

type RolesTableProps = {};

export const RolesTable = ({ }: RolesTableProps) => {

    const [roles, setRoles] = useState<Role[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const fetchRoles = async () => {
        setIsLoading(true);
        const { results, total: _total } = await iamService.searchRoles({});
        setRoles(results);
        setTotal(_total);
        setIsLoading(false);
    };

    useEffect(() => {
        fetchRoles();
    }, []);

    const [permissions, setPermissions] = useState<Permission[]>([]);
    const [isOpenPermissionModal, setIsOpenPermissionModal] = useState<boolean>(true);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedRole, setSelectedRole] = useState<Role | undefined>(undefined);

    const fetchPermssions = async () => {
        const perms = await iamService.searchPermissions({});
        setPermissions(perms);
    };

    useEffect(() => {
        fetchRoles();
        fetchPermssions();
    }, []);

    const onCreateRole = async (req: RoleCreateRequest) => {
        await iamService.createRole({ ...req });
        fetchRoles();
        onClose();
    };

    const onUpdateRole = async (id: number, req: RoleUpdateRequest) => {
        await iamService.updateRoleById(id, { ...req });
        fetchRoles();
        onClose();
    };

    const handleEditButtonClick = (role: Role) => {
        setSelectedRole(role);
        onOpen();
    };

    const handleAddButtonClick = () => {
        setSelectedRole(undefined);
        onOpen();
    };

    const handlePermissionButtonClick = (role: Role) => {
        setSelectedRole(role);
        setIsOpenPermissionModal(true);
    };

    if (!isLoading && total === 0) {
        return (
            <Box textAlign={'center'} mt={4}>
                No roles found.
            </Box>
        )
    }

    if (isLoading) {
        return <LoadingSpinner />
    }

    return (
        <>
            <Flex>
                <Button onClick={handleAddButtonClick} ml={'auto'} colorScheme="teal">Add Role</Button>
            </Flex>
            <Table variant="simple">
                <TableCaption textAlign={'left'}>Total Roles: {total}</TableCaption>
                <Thead>
                    <Tr>
                        <Th>ID</Th>
                        <Th>Name</Th>
                        <Th>Description</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {roles.map(role => (
                        <Tr key={role.id}>
                            <Td>{role.id}</Td>
                            <Td>{role.name}</Td>
                            <Td>{role.description}</Td>
                            <Td>
                                <Flex>
                                    <Button mr={2} size="sm" onClick={() => handleEditButtonClick(role)}>
                                        Edit
                                    </Button>
                                    <Button size="sm" onClick={() => handlePermissionButtonClick(role)}>
                                        Permissions
                                    </Button>
                                </Flex>
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
                <AddEditIAMModal
                    entity="Role"
                    isOpen={isOpen}
                    onClose={onClose}
                    onCreate={onCreateRole}
                    onUpdate={onUpdateRole}
                    iam={selectedRole}
                />
                {selectedRole && <ManagePermissionsModal
                    role={selectedRole}
                    isOpen={isOpenPermissionModal}
                    onClose={() => setIsOpenPermissionModal(false)}
                    permissions={permissions}
                />}
            </Table>
        </>
    )
};
